import { z, type ZodType } from 'zod'

/**
 * Wraps the given field spec with a union that allows for the empty string ("") which
 * is converted to `undefined` on processing.
 *
 * This helps form fields that are optional; if there is a blank string we just
 * do not want to send the result at all.
 *
 * @param baseType base shape of the field
 */
export function undefinedWhenEmpty<T extends ZodType>(baseType: T) {
  return z.union([z.literal('').transform(() => undefined), baseType])
}

/**
 * Traverses a nested schema to find the field schema at a given path.
 * Supports dot notation like "contract.startDate".
 */
export function getFieldSchema(
  schema: z.ZodObject | undefined,
  path: string
): z.ZodType | undefined {
  if (!schema) return undefined

  const segments = path.split('.')
  let current: z.ZodObject | undefined = schema

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    if (!current || !(segment in current.shape)) {
      return undefined
    }

    const field: z.ZodType = current.shape[segment]

    // If this is the last segment, return the field schema
    if (i === segments.length - 1) {
      return field
    }

    // Otherwise, traverse into nested object
    current = field instanceof z.ZodObject ? field : undefined
  }

  return undefined
}

/**
 * Checks if a field is required in a schema by testing if value that would only
 * be accepted when the field is optional, is parsed successfully.
 * Supports dot notation paths like "contract.startDate".
 */
export function isFieldRequired(
  schema: z.ZodObject | undefined,
  fieldName: string
): boolean {
  if (!schema) {
    return false
  }
  const fieldSchema = getFieldSchema(schema, fieldName)
  if (!fieldSchema) {
    return false
  }

  if (fieldSchema instanceof z.ZodNull || fieldSchema instanceof z.ZodUndefined) {
    return false
  }

  const valueForOptionalField = fieldSchema instanceof z.ZodArray ? [] : undefined
  return !fieldSchema.safeParse(valueForOptionalField).success
}

export function getActiveSchema<T extends z.ZodDiscriminatedUnion>(
  schema: T,
  discriminatorValue: z.infer<T['def']['discriminator']>
): T['options'][number] | undefined {
  const discriminatorKey = schema.def.discriminator

  return schema.options.find(option => {
    const discriminatorField = (option as z.ZodObject).shape[discriminatorKey]

    if (discriminatorField instanceof z.ZodLiteral) {
      return discriminatorField.value === discriminatorValue
    }

    if (discriminatorField instanceof z.ZodEnum) {
      return discriminatorField.options.includes(discriminatorValue as string)
    }

    return false
  }) as T['options'][number] | undefined
}
